import {
    Directive,
    EmbeddedViewRef,
    Input,
    OnDestroy,
    Renderer2,
    TemplateRef,
    ViewContainerRef,
    inject
} from '@angular/core';
import { FeatureConfig, FeatureKey } from './tenant-config.model';
import { TenantConfigService } from './tenant-config.service';

interface OriginalElementState {
    tabindex: string | null;
    ariaDisabled: string | null;
    dataFeatureState: string | null;
    disabled?: boolean;
}

/**
 * Directiva estructural para mostrar, ocultar o deshabilitar contenido
 * según una feature configurada para el tenant actual.
 *
 * Estados soportados:
 *   FeatureConfig.ENABLED  -> renderiza normal
 *   FeatureConfig.DISABLED -> renderiza visible, pero bloqueado
 *   FeatureConfig.HIDDEN   -> no renderiza
 *
 * Uso:
 *   <button *tenantFeature="'orders.cancel'">Cancelar</button>
 *   <a *tenantFeature="'sideMenu.home'" routerLink="/home">Home</a>
 *
 * Standalone: importar la directiva en el componente / módulo donde se use.
 */
@Directive({
    selector: '[tenantFeature]',
    standalone: true
})
export class TenantFeatureDirective implements OnDestroy {
    private readonly templateRef = inject(TemplateRef<unknown>);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly tenantConfig = inject(TenantConfigService);
    private readonly renderer = inject(Renderer2);

    private viewRef: EmbeddedViewRef<unknown> | null = null;
    private featureKey: FeatureKey | null = null;

    private readonly originalStateByElement = new Map<HTMLElement, OriginalElementState>();
    private readonly removeBlockingListeners: Array<() => void> = [];

    @Input()
    set tenantFeature(featureKey: FeatureKey) {
        this.featureKey = featureKey;
        this.applyFeatureState();
    }

    ngOnDestroy(): void {
        this.clearView();
    }

    private applyFeatureState(): void {
        if (!this.featureKey) {
            return;
        }

        const state = this.tenantConfig.getFeatureConfig(this.featureKey);

        if (state === FeatureConfig.HIDDEN) {
            this.clearView();
            return;
        }

        this.ensureView();
        this.resetRenderedElements();

        if (state === FeatureConfig.DISABLED) {
            this.disableRenderedElements();
            return;
        }

        this.enableRenderedElements();
    }

    private ensureView(): void {
        if (this.viewRef) {
            return;
        }

        this.viewRef = this.viewContainerRef.createEmbeddedView(this.templateRef);
        this.viewRef.detectChanges();
    }

    private clearView(): void {
        this.removeListeners();
        this.originalStateByElement.clear();
        this.viewContainerRef.clear();
        this.viewRef = null;
    }

    private enableRenderedElements(): void {
        this.getRootElements().forEach((element) => {
            this.renderer.removeClass(element, 'tenant-feature-disabled');
            this.renderer.setAttribute(element, 'data-feature-state', FeatureConfig.ENABLED);
        });
    }

    private disableRenderedElements(): void {
        const rootElements = this.getRootElements();
        const interactiveElements = this.getInteractiveElements();

        rootElements.forEach((element) => {
            this.saveOriginalState(element);

            this.renderer.addClass(element, 'tenant-feature-disabled');
            this.renderer.setAttribute(element, 'aria-disabled', 'true');
            this.renderer.setAttribute(element, 'data-feature-state', FeatureConfig.DISABLED);
        });

        interactiveElements.forEach((element) => {
            this.saveOriginalState(element);

            this.renderer.setAttribute(element, 'aria-disabled', 'true');
            this.renderer.setAttribute(element, 'tabindex', '-1');

            if (this.supportsNativeDisabled(element)) {
                this.renderer.setProperty(element, 'disabled', true);
            }
        });

        this.addBlockingListeners(rootElements);
    }

    private resetRenderedElements(): void {
        this.removeListeners();

        this.originalStateByElement.forEach((originalState, element) => {
            this.restoreAttribute(element, 'tabindex', originalState.tabindex);
            this.restoreAttribute(element, 'aria-disabled', originalState.ariaDisabled);
            this.restoreAttribute(element, 'data-feature-state', originalState.dataFeatureState);

            this.renderer.removeClass(element, 'tenant-feature-disabled');

            if (
                originalState.disabled !== undefined &&
                this.supportsNativeDisabled(element)
            ) {
                this.renderer.setProperty(element, 'disabled', originalState.disabled);
            }
        });

        this.originalStateByElement.clear();
    }

    private saveOriginalState(element: HTMLElement): void {
        if (this.originalStateByElement.has(element)) {
            return;
        }

        this.originalStateByElement.set(element, {
            tabindex: element.getAttribute('tabindex'),
            ariaDisabled: element.getAttribute('aria-disabled'),
            dataFeatureState: element.getAttribute('data-feature-state'),
            disabled: this.supportsNativeDisabled(element)
                ? element.disabled
                : undefined
        });
    }

    private restoreAttribute(
        element: HTMLElement,
        attributeName: string,
        originalValue: string | null
    ): void {
        if (originalValue === null) {
            this.renderer.removeAttribute(element, attributeName);
            return;
        }

        this.renderer.setAttribute(element, attributeName, originalValue);
    }

    private getRootElements(): HTMLElement[] {
        if (!this.viewRef) {
            return [];
        }

        return this.viewRef.rootNodes.filter(
            (node): node is HTMLElement => node?.nodeType === Node.ELEMENT_NODE
        );
    }

    private getInteractiveElements(): HTMLElement[] {
        const interactiveSelector = [
            'a[href]',
            'button',
            'input',
            'select',
            'textarea',
            'summary',
            '[tabindex]',
            '[role="button"]',
            '[role="link"]',
            '[role="menuitem"]',
            '[role="tab"]',
            '[routerLink]',
            '[routerlink]',
            '[ng-reflect-router-link]'
        ].join(',');

        const elements = new Set<HTMLElement>();

        this.getRootElements().forEach((rootElement) => {
            if (rootElement.matches(interactiveSelector)) {
                elements.add(rootElement);
            }

            rootElement
                .querySelectorAll<HTMLElement>(interactiveSelector)
                .forEach((childElement) => elements.add(childElement));
        });

        return Array.from(elements);
    }

    private addBlockingListeners(rootElements: HTMLElement[]): void {
        this.removeListeners();

        const blockEvent = (event: Event): void => {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        };

        rootElements.forEach((element) => {
            element.addEventListener('click', blockEvent, true);
            element.addEventListener('mousedown', blockEvent, true);
            element.addEventListener('mouseup', blockEvent, true);
            element.addEventListener('touchstart', blockEvent, true);
            element.addEventListener('keydown', blockEvent, true);

            this.removeBlockingListeners.push(() => {
                element.removeEventListener('click', blockEvent, true);
                element.removeEventListener('mousedown', blockEvent, true);
                element.removeEventListener('mouseup', blockEvent, true);
                element.removeEventListener('touchstart', blockEvent, true);
                element.removeEventListener('keydown', blockEvent, true);
            });
        });
    }

    private removeListeners(): void {
        while (this.removeBlockingListeners.length > 0) {
            const removeListener = this.removeBlockingListeners.pop();
            removeListener?.();
        }
    }

    private supportsNativeDisabled(
        element: HTMLElement
    ): element is
        | HTMLButtonElement
        | HTMLInputElement
        | HTMLSelectElement
        | HTMLTextAreaElement
        | HTMLOptGroupElement
        | HTMLOptionElement
        | HTMLFieldSetElement {
        return (
            element instanceof HTMLButtonElement ||
            element instanceof HTMLInputElement ||
            element instanceof HTMLSelectElement ||
            element instanceof HTMLTextAreaElement ||
            element instanceof HTMLOptGroupElement ||
            element instanceof HTMLOptionElement ||
            element instanceof HTMLFieldSetElement
        );
    }
}